import React, { useEffect, useRef, useState } from "react";
import { Card } from "../../tremor/Card";
import { Label } from "../../tremor/Text";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from "../../tremor/Select";
import { Input } from "../../tremor/Input";
import { Button } from "../../tremor/Button";
import { Switch } from "../../tremor/Switch";
import { Tabs, TabsList, TabsTrigger } from "../../tremor/Tabs";
import { Bars3BottomLeftIcon, Bars3BottomRightIcon, Bars3Icon } from "@heroicons/react/24/outline";
const chartTypes = [
    "line",
    "bar",
    "area",
    "metric",
    "table",
    "pie",
    "doughnut",
    "bubble",
    "polarArea",
    "radar",
    "scatter",
    "funnel",
].map((a) => ({ label: a, value: a }));
const PositionPicker = ({ value, onValueChange }) => {
    return (React.createElement(Tabs, { defaultValue: "top", value: value || "top", onValueChange: onValueChange },
        React.createElement(TabsList, { variant: "solid" },
            React.createElement(TabsTrigger, { value: "top" }, "Top"),
            React.createElement(TabsTrigger, { value: "left" }, "Left"),
            React.createElement(TabsTrigger, { value: "bottom" }, "Bottom"),
            React.createElement(TabsTrigger, { value: "right" }, "Right"))));
};
const AlignmentPicker = ({ value, onValueChange }) => {
    return (React.createElement(Tabs, { defaultValue: "start", value: value || "start", onValueChange: onValueChange },
        React.createElement(TabsList, { variant: "solid" },
            React.createElement(TabsTrigger, { value: "start" },
                React.createElement(Bars3BottomLeftIcon, { className: "onvo-size-4" })),
            React.createElement(TabsTrigger, { value: "center" },
                React.createElement(Bars3Icon, { className: "onvo-size-4" })),
            React.createElement(TabsTrigger, { value: "end" },
                React.createElement(Bars3BottomRightIcon, { className: "onvo-size-4" })))));
};
export const WidgetWizard = ({ columns, onChange, config }) => {
    const [type, setType] = useState(chartTypes[0].value);
    const [labels, setLabels] = useState("");
    const [title, setTitle] = useState({
        'text': ""
    });
    const [subtitle, setSubtitle] = useState({
        'text': ""
    });
    const [xAxis, setXAxis] = useState({
        'display': false
    });
    const [yAxis, setYAxis] = useState({
        'display': false
    });
    const [legend, setLegend] = useState({
        'display': false
    });
    const [datasets, setDatasets] = useState([]);
    const prevConfig = useRef(null);
    const constructCode = () => {
        let newConfig = {
            'type': type,
            'data': {
                'labels': labels,
                'datasets': datasets.map((d) => ({
                    'label': d.title,
                    'type': d.type,
                    'data': d.data,
                    'backgroundColor': d.backgroundColor,
                    'borderColor': d.borderColor,
                    'borderWidth': d.borderWidth,
                }))
            },
            'options': {
                'responsive': true,
                'maintainAspectRatio': false,
                'plugins': {
                    'title': title,
                    'subtitle': subtitle,
                    'legend': legend
                },
                'scales': {
                    'x': xAxis,
                    'y': yAxis
                }
            }
        };
        if (JSON.stringify(prevConfig.current) !== JSON.stringify(newConfig)) {
            // @ts-ignore
            prevConfig.current = newConfig;
            onChange(newConfig);
        }
    };
    useEffect(() => {
        if (config && JSON.stringify(prevConfig.current) !== JSON.stringify(config)) {
            prevConfig.current = config;
            setType(config.type);
            setLabels(config.data.labels);
            setTitle(config.options.plugins.title);
            setSubtitle(config.options.plugins.subtitle);
            setXAxis(config.options.scales.x);
            setYAxis(config.options.scales.y);
            setLegend(config.options.plugins.legend);
            setDatasets(config.data.datasets);
        }
    }, [config]);
    useEffect(() => {
        constructCode();
    }, [
        type,
        datasets,
        title,
        subtitle,
        xAxis,
        yAxis,
        labels
    ]);
    return (React.createElement("div", { className: "onvo-w-full onvo-flex onvo-flex-col onvo-gap-2" },
        React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-justify-between" },
            React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0" }, "Chart type"),
            React.createElement(Select, { value: type, onValueChange: setType },
                React.createElement(SelectTrigger, null,
                    React.createElement(SelectValue, { placeholder: "Select" })),
                React.createElement(SelectContent, null, chartTypes.map((item) => (React.createElement(SelectItem, { key: item.value, value: item.value }, item.label)))))),
        React.createElement("div", { className: "onvo-w-full onvo-flex onvo-flex-col onvo-flex-grow onvo-gap-2" },
            React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-justify-between" },
                React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0" }, "Datasets"),
                React.createElement(Button, { variant: "secondary", onClick: () => setDatasets([
                        ...datasets,
                        {
                            title: "Dataset " + (datasets.length + 1),
                            data: "",
                            type: type || "line"
                        },
                    ]) }, "Add dataset")),
            datasets.map((a, idx) => (React.createElement(Card, { key: "dataset-" + (idx + 1), className: "onvo-flex onvo-flex-col onvo-gap-2" },
                React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-3" },
                    React.createElement(Label, { className: "onvo-flex-shrink-0" }, "Column"),
                    React.createElement(Select, { value: a.data, onValueChange: val => {
                            setDatasets(datasets.map((a, i) => {
                                if (i === idx) {
                                    return Object.assign(Object.assign({}, a), { data: val });
                                }
                                return a;
                            }));
                        } },
                        React.createElement(SelectTrigger, null,
                            React.createElement(SelectValue, { placeholder: "Select" })),
                        React.createElement(SelectContent, null, columns.map((item) => (React.createElement(SelectItem, { key: item, value: `{{data['${item}']}}` }, item)))))),
                React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-3" },
                    React.createElement(Label, { className: "onvo-flex-grow onvo-flex-shrink-0" }, "Title"),
                    React.createElement(Input, { placeholder: "Title", value: a.title, onChange: (e) => {
                            setDatasets(datasets.map((a, i) => {
                                if (i === idx) {
                                    return Object.assign(Object.assign({}, a), { title: e.target.value });
                                }
                                return a;
                            }));
                        } }),
                    React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0" }, "Type"),
                    React.createElement(Select, { value: a.type, onValueChange: val => {
                            setDatasets(datasets.map((a, i) => {
                                if (i === idx) {
                                    return Object.assign(Object.assign({}, a), { type: val });
                                }
                                return a;
                            }));
                        } },
                        React.createElement(SelectTrigger, null,
                            React.createElement(SelectValue, { placeholder: "Select" })),
                        React.createElement(SelectContent, null, chartTypes.map((item) => (React.createElement(SelectItem, { key: item.value, value: item.value }, item.label)))))),
                React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-3" },
                    React.createElement(Label, { className: "onvo-flex-shrink-0" }, "Background"),
                    React.createElement(Input, { placeholder: "#ff1245", value: a.backgroundColor, onChange: (e) => {
                            setDatasets(datasets.map((a, i) => {
                                if (i === idx) {
                                    return Object.assign(Object.assign({}, a), { backgroundColor: e.target.value });
                                }
                                return a;
                            }));
                        } }),
                    React.createElement(Label, { className: "onvo-flex-shrink-0" }, "Border"),
                    React.createElement(Input, { placeholder: "#ff0000", value: a.borderColor, onChange: (e) => {
                            setDatasets(datasets.map((a, i) => {
                                if (i === idx) {
                                    return Object.assign(Object.assign({}, a), { borderColor: e.target.value });
                                }
                                return a;
                            }));
                        } }),
                    React.createElement(Label, { className: "onvo-flex-shrink-0" }, "Border width"),
                    React.createElement(Input, { type: "number", placeholder: "2", value: a.borderWidth, onChange: (e) => {
                            setDatasets(datasets.map((a, i) => {
                                if (i === idx) {
                                    return Object.assign(Object.assign({}, a), { borderWidth: Number(e.target.value) });
                                }
                                return a;
                            }));
                        } })),
                React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-justify-end onvo-gap-2" },
                    React.createElement(Button, { variant: "destructive", onClick: () => {
                            setDatasets(datasets.filter((a, i) => i !== idx));
                        } }, "Remove datasource")))))),
        React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-2" },
            React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0" }, "Labels"),
            React.createElement(Select, { value: labels, onValueChange: val => {
                    setLabels(val);
                } },
                React.createElement(SelectTrigger, null,
                    React.createElement(SelectValue, { placeholder: "Select" })),
                React.createElement(SelectContent, null, columns.map((item) => (React.createElement(SelectItem, { key: item, value: `{{data['${item}']}}` }, item)))))),
        React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-justify-between" },
            React.createElement(Label, null, "Title"),
            React.createElement(Tabs, { defaultValue: "false", value: title.display ? "true" : "false", onValueChange: (val) => {
                    setTitle(t => (Object.assign(Object.assign({}, t), { display: val === "true" })));
                } },
                React.createElement(TabsList, { variant: "solid" },
                    React.createElement(TabsTrigger, { value: "true" }, "Visible"),
                    React.createElement(TabsTrigger, { value: "false" }, "Hidden")))),
        title.display && (React.createElement("div", { className: "onvo-flex onvo-flex-col onvo-gap-2" },
            React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-2" },
                React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0" }, "Chart title"),
                React.createElement(Input, { value: title.text, placeholder: "Chart title", onChange: (e) => setTitle(t => (Object.assign(Object.assign({}, t), { text: e.target.value }))) })),
            React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-2" },
                React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0" }, "Position"),
                React.createElement(PositionPicker, { value: title.position || "start", onValueChange: val => setTitle(t => (Object.assign(Object.assign({}, t), { position: val }))) }),
                React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0 onvo-text-right" }, "Align"),
                React.createElement(AlignmentPicker, { value: title.align || "center", onValueChange: val => setTitle(t => (Object.assign(Object.assign({}, t), { align: val }))) })))),
        React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-justify-between" },
            React.createElement(Label, null, "Subtitle"),
            React.createElement(Tabs, { defaultValue: "false", value: subtitle.display ? "true" : "false", onValueChange: (val) => {
                    setSubtitle(t => (Object.assign(Object.assign({}, t), { display: val === "true" })));
                } },
                React.createElement(TabsList, { variant: "solid" },
                    React.createElement(TabsTrigger, { value: "true" }, "Visible"),
                    React.createElement(TabsTrigger, { value: "false" }, "Hidden")))),
        subtitle.display && (React.createElement("div", { className: "onvo-flex onvo-flex-col onvo-gap-2" },
            React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-2" },
                React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0" }, "Chart subtitle"),
                React.createElement(Input, { placeholder: "Chart subtitle", value: subtitle.text, onChange: (e) => setSubtitle(t => (Object.assign(Object.assign({}, t), { text: e.target.value }))) })),
            React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-2" },
                React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0" }, "Position"),
                React.createElement(PositionPicker, { value: subtitle.position || "start", onValueChange: val => setSubtitle(t => (Object.assign(Object.assign({}, t), { position: val }))) }),
                React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0 onvo-text-right" }, "Align"),
                React.createElement(AlignmentPicker, { value: subtitle.align || "center", onValueChange: val => setSubtitle(t => (Object.assign(Object.assign({}, t), { align: val }))) })))),
        React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-justify-between" },
            React.createElement(Label, null, "Legend"),
            React.createElement(Tabs, { defaultValue: "false", value: legend.display ? "true" : "false", onValueChange: (val) => {
                    setLegend(t => (Object.assign(Object.assign({}, t), { display: val === "true" })));
                } },
                React.createElement(TabsList, { variant: "solid" },
                    React.createElement(TabsTrigger, { value: "true" }, "Visible"),
                    React.createElement(TabsTrigger, { value: "false" }, "Hidden")))),
        legend.display && (React.createElement("div", { className: "onvo-flex onvo-flex-col onvo-gap-2" },
            React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-2" },
                React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0" }, "Position"),
                React.createElement(PositionPicker, { value: legend.position || "start", onValueChange: val => setLegend(t => (Object.assign(Object.assign({}, t), { position: val }))) }),
                React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0 onvo-text-right" }, "Align"),
                React.createElement(AlignmentPicker, { value: legend.align || "center", onValueChange: val => setLegend(t => (Object.assign(Object.assign({}, t), { align: val }))) })))),
        React.createElement("div", { className: "onvo-flex onvo-flex-col onvo-gap-2 onvo-mt-2" },
            React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-2" },
                React.createElement(Label, { className: "onvo-flex-grow onvo-flex-shrink-0" }, "Show x axis"),
                React.createElement(Switch, { checked: xAxis.display, onCheckedChange: (val) => setXAxis(t => (Object.assign(Object.assign({}, t), { display: val }))) }),
                xAxis.display && (React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-2" },
                    React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0" }, "X axis title"),
                    React.createElement(Input, { value: xAxis.text || "", onChange: (e) => setXAxis(t => (Object.assign(Object.assign({}, t), { text: e.target.value }))), placeholder: "Date in DD/MM/YYYY format" })))),
            React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-2" },
                React.createElement(Label, { className: "onvo-flex-grow onvo-flex-shrink-0" }, "Show y axis"),
                React.createElement(Switch, { checked: yAxis.display, onCheckedChange: (val) => setYAxis(t => (Object.assign(Object.assign({}, t), { display: val }))) }),
                yAxis.display && (React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-2" },
                    React.createElement(Label, { className: "onvo-w-24 onvo-flex-shrink-0" }, "Y axis title"),
                    React.createElement(Input, { value: yAxis.text, onChange: (e) => setYAxis(t => (Object.assign(Object.assign({}, t), { text: e.target.value }))), placeholder: "No of users" })))))));
};
//# sourceMappingURL=WidgetWizard.js.map